import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, MessageSquare } from "lucide-react";
import { IssueCard } from "./IssueCard";
import { NewIssueForm } from "./NewIssueForm";
import { CommentThread } from "./CommentThread";
import { useIssuesAndComments } from "@/hooks/useIssuesAndComments";
import { toast } from "sonner";

interface IssuesPanelProps {
  medicalTextExampleId: string;
}

export function IssuesPanel({ medicalTextExampleId }: IssuesPanelProps) {
  const [showNewIssueForm, setShowNewIssueForm] = useState(false);
  const [selectedIssueId, setSelectedIssueId] = useState<string | undefined>();
  const [showActiveOnly, setShowActiveOnly] = useState(true);

  const {
    activeIssues,
    resolvedIssues,
    isLoadingIssues,
    createIssue,
    isCreatingIssue,
    resolveIssue,
    isResolvingIssue,
    reopenIssue,
    isReopeningIssue,
    comments,
    isLoadingComments,
    createComment,
    isCreatingComment,
  } = useIssuesAndComments(medicalTextExampleId, selectedIssueId);

  const displayedIssues = showActiveOnly ? activeIssues : resolvedIssues;
  const selectedIssue = [...activeIssues, ...resolvedIssues].find((issue) => issue.id === selectedIssueId);

  const handleCreateIssue = async (data: any) => {
    try {
      console.log("Creating issue with data:", data);

      // Use the promise-based createIssue from the hook
      await createIssue(data);

      console.log("Issue created successfully, hiding form");
      setShowNewIssueForm(false);

      toast.success("Issue created successfully");
    } catch (error) {
      console.error("Error creating issue:", error);
      toast.error("Failed to create issue. Please try again.");
    }
  };

  const getCommentCount = (issueId: string) => {
    if (selectedIssueId === issueId) {
      return comments.length;
    }
    return 0; // We don't load comment counts for non-selected issues to avoid unnecessary queries
  };

  if (isLoadingIssues) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Issues & Task Log</h2>
        </div>
        <div className="text-center text-gray-500 py-8">Loading issues...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Issues & Task Log</h2>
        {!showNewIssueForm && (
          <Button size="sm" onClick={() => setShowNewIssueForm(true)} type="button">
            <Plus className="w-4 h-4 mr-2" />
            New Issue
          </Button>
        )}
      </div>

      {/* Status Filter */}
      <div className="flex gap-2">
        <Button
          variant={showActiveOnly ? "default" : "outline"}
          size="sm"
          onClick={() => setShowActiveOnly(true)}
          type="button"
        >
          Active
          {activeIssues.length > 0 && (
            <Badge variant="secondary" className="ml-2">
              {activeIssues.length}
            </Badge>
          )}
        </Button>
        <Button
          variant={!showActiveOnly ? "default" : "outline"}
          size="sm"
          onClick={() => setShowActiveOnly(false)}
          type="button"
        >
          Resolved
          {resolvedIssues.length > 0 && (
            <Badge variant="secondary" className="ml-2">
              {resolvedIssues.length}
            </Badge>
          )}
        </Button>
      </div>

      {/* New Issue Form */}
      {showNewIssueForm && (
        <NewIssueForm
          medicalTextExampleId={medicalTextExampleId}
          onSubmit={handleCreateIssue}
          onCancel={() => setShowNewIssueForm(false)}
          isSubmitting={isCreatingIssue}
        />
      )}

      {/* Issues List */}
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {displayedIssues.length === 0 ? (
          <div className="text-center text-gray-500 py-8 border rounded-lg">
            {showActiveOnly ? "No active issues" : "No resolved issues"}
          </div>
        ) : (
          displayedIssues.map((issue) => (
            <IssueCard
              key={issue.id}
              issue={issue}
              onResolve={resolveIssue}
              onReopen={reopenIssue}
              onClick={() => setSelectedIssueId(selectedIssueId === issue.id ? undefined : issue.id)}
              isSelected={selectedIssueId === issue.id}
              isResolving={isResolvingIssue}
              isReopening={isReopeningIssue}
              commentCount={getCommentCount(issue.id)}
              userProfile={(issue as any).created_by_profile}
              resolvedByProfile={(issue as any).resolved_by_profile}
            />
          ))
        )}
      </div>

      {/* Comment Thread */}
      {selectedIssue && (
        <div className="border-t pt-4">
          <div className="flex items-center gap-2 mb-4">
            <MessageSquare className="w-5 h-5" />
            <h3 className="font-medium">Comments</h3>
            <Badge variant="secondary">{comments.length}</Badge>
          </div>

          <CommentThread
            issueId={selectedIssue.id}
            comments={comments}
            isLoading={isLoadingComments}
            onAddComment={createComment}
            isAddingComment={isCreatingComment}
          />
        </div>
      )}
    </div>
  );
}
